/**
 * @workspace/copy
 *
 * User-facing strings that MUST be identical across the web app and the
 * mobile app. The web app's full i18n (`artifacts/bookaclassic/src/i18n`)
 * still owns the long-tail of marketing/page copy; this package owns the
 * short-list of strings where a divergence is a product bug:
 *
 *   - Booking lifecycle status labels (renter & host see the same wording).
 *   - Common booking error messages surfaced from the API.
 *   - Key CTA labels that anchor user mental models ("Book it", "Decline",
 *     "Send message", "Switch to host").
 *
 * Copy is keyed by string literals (TypeScript-typed). When you need to add
 * a key, add it here AND update the matching translations on the web side.
 *
 * Translations: this package ships English. Other locales are layered on
 * top by the web app's i18n loader using the same key names.
 */
export * from "./driving-progress";

/* ------------------------------------------------------------------ */
/* Booking statuses                                                    */
/* ------------------------------------------------------------------ */

/** Canonical booking statuses persisted by the api-server. */
export const BOOKING_STATUSES = [
  "pending",
  "approved",
  "confirmed",
  "in_progress",
  "completed",
  "declined",
  "cancelled_by_renter",
  "cancelled_by_host",
  "expired",
] as const;
export type BookingStatus = typeof BOOKING_STATUSES[number];

export function isBookingStatus(value: unknown): value is BookingStatus {
  return typeof value === "string" && (BOOKING_STATUSES as readonly string[]).includes(value);
}

/** Display label for a booking status (renter perspective). */
export const BOOKING_STATUS_LABEL: Readonly<Record<BookingStatus, string>> = {
  pending: "Pending host approval",
  approved: "Approved",
  confirmed: "Confirmed",
  in_progress: "Trip in progress",
  completed: "Completed",
  declined: "Declined",
  cancelled_by_renter: "Cancelled",
  cancelled_by_host: "Cancelled by host",
  expired: "Expired",
};

/* ------------------------------------------------------------------ */
/* Booking error messages (API → user)                                 */
/* ------------------------------------------------------------------ */

export const BOOKING_ERROR_KEYS = [
  "dates_unavailable",
  "renter_not_verified",
  "payment_required",
  "host_inactive",
  "listing_paused",
  "listing_not_found",
  "outside_country",
  "too_close_to_pickup",
  "rate_limited",
  "unknown_error",
] as const;
export type BookingErrorKey = typeof BOOKING_ERROR_KEYS[number];

export const BOOKING_ERROR_MESSAGE: Readonly<Record<BookingErrorKey, string>> = {
  dates_unavailable: "These dates are no longer available.",
  renter_not_verified: "Please complete identity verification before booking.",
  payment_required: "Add a payment method to continue.",
  host_inactive: "This host isn't accepting new bookings right now.",
  listing_paused: "This listing is currently paused.",
  listing_not_found: "We couldn't find that listing.",
  outside_country: "This car can't be rented outside its country.",
  too_close_to_pickup: "It's too close to pickup to book this car.",
  rate_limited: "Too many attempts. Please try again in a moment.",
  unknown_error: "Something went wrong. Please try again.",
};

/* ------------------------------------------------------------------ */
/* Key CTAs                                                            */
/* ------------------------------------------------------------------ */

export const CTA_KEYS = [
  "book_it",
  "request_to_book",
  "approve",
  "decline",
  "cancel",
  "send_message",
  "switch_to_host",
  "switch_to_guest",
  "list_your_car",
  "save_changes",
  "publish_listing",
] as const;
export type CtaKey = typeof CTA_KEYS[number];

export const CTA_LABEL: Readonly<Record<CtaKey, string>> = {
  book_it: "Book it",
  request_to_book: "Request to book",
  approve: "Approve",
  decline: "Decline",
  cancel: "Cancel",
  send_message: "Send message",
  switch_to_host: "Switch to host",
  switch_to_guest: "Switch to guest",
  list_your_car: "List your car",
  save_changes: "Save changes",
  publish_listing: "Publish listing",
};

/* ------------------------------------------------------------------ */
/* MFA / two-factor authentication                                     */
/* ------------------------------------------------------------------ */

/**
 * Human-readable prefix for the friendly name attached to a user's TOTP
 * authenticator factor during 2FA enrolment. The full name returned by
 * `mfaTotpFriendlyName()` is this prefix plus a unique suffix.
 *
 * This value MUST be defined in exactly one place and used identically by
 * both the web app (SettingsPage) and the mobile app (MfaSection). Hardcoding
 * it inline on each platform is how the two drifted and how a future rename
 * could silently break enrolment.
 */
export const MFA_TOTP_FRIENDLY_NAME_PREFIX = "Authenticator App";

/**
 * Friendly name to pass to `supabase.auth.mfa.enroll({ friendlyName })`.
 *
 * Supabase enforces per-user uniqueness on the factor friendly name, so a
 * fixed string would collide with any abandoned, unverified factor from a
 * previous attempt — exactly the "factor already exists" failure this
 * indirection exists to prevent. We therefore append a collision-resistant
 * suffix (timestamp + random nonce) so each enrolment attempt gets a fresh,
 * never-before-used name. This guarantees enrolment cannot collide even if
 * the unverified-factor cleanup is skipped, and both platforms get the
 * identical strategy by calling this single function.
 */
export function mfaTotpFriendlyName(): string {
  const suffix = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  return `${MFA_TOTP_FRIENDLY_NAME_PREFIX} ${suffix}`;
}

/* ------------------------------------------------------------------ */
/* Lookup                                                              */
/* ------------------------------------------------------------------ */

/**
 * Generic lookup helper. Returns `undefined` for unknown keys so the caller
 * can choose between a fallback string and a logged warning.
 */
export function lookupCopy<K extends string, V extends string>(
  table: Readonly<Record<K, V>>,
  key: string,
): V | undefined {
  return (table as Record<string, V>)[key];
}

/**
 * Booking-status → label resolver. Falls back to the raw status string
 * (humanised) when the status is unknown so we never render an empty
 * string in the UI.
 */
export function bookingStatusLabel(status: string | null | undefined): string {
  if (!status) return "";
  if (isBookingStatus(status)) return BOOKING_STATUS_LABEL[status];
  return String(status).replace(/_/g, " ");
}

/**
 * Booking-error → message resolver. Falls back to the generic
 * `unknown_error` message.
 */
export function bookingErrorMessage(key: string | null | undefined): string {
  const found = key ? lookupCopy(BOOKING_ERROR_MESSAGE, key) : undefined;
  return found ?? BOOKING_ERROR_MESSAGE.unknown_error;
}

/* ------------------------------------------------------------------ */
/* Host dashboard — shared labels (web + mobile)                       */
/* ------------------------------------------------------------------ */
/*
 * These constants are the single source of truth for labels that appear
 * on BOTH the web host dashboard (DashboardLayout / DashboardPage) and
 * the mobile Today screen (today.tsx).  The web side drives them through
 * the i18n pipeline (en.json keys noted below); the mobile app (which is
 * English-only) consumes them directly.  Any wording change should be
 * made here AND in the matching en.json key so the platforms stay in sync.
 *
 * Web i18n key cross-references:
 *   HOST_NAV_MY_LISTINGS     → nav.my_listings
 *   HOST_NAV_MY_GARAGE       → nav.my_garage
 *   HOST_NAV_VEHICLES        → nav.vehicles
 *   HOST_NAV_BOOKINGS        → nav.bookings
 *   HOST_NAV_EARNINGS        → nav.earnings
 *   HOST_NAV_REVIEWS         → nav.reviews
 *   HOST_NAV_NOTIFICATIONS   → nav.notifications
 *   UPCOMING_BOOKINGS_HEADING → dashboard.upcoming_bookings
 *   VIEW_ALL                 → common.view_all
 *   MANAGE_AVAILABILITY_CTA  → dashboard.manage_availability
 *   CONNECT_STRIPE_CTA       → dashboard.connect_stripe
 */

/** Canonical host entry-point label for the owner's listings. */
export const HOST_NAV_MY_LISTINGS = "My Listings";

/** Canonical destination/page label for the host's listings collection. */
export const HOST_NAV_MY_GARAGE = "My Garage";

/** Sidebar nav label for the Vehicles (listings) section. */
export const HOST_NAV_VEHICLES = "Vehicles";

/** Sidebar nav label for the Bookings section. */
export const HOST_NAV_BOOKINGS = "Bookings";

/** Sidebar nav label for the Earnings section. */
export const HOST_NAV_EARNINGS = "Earnings";

/** Sidebar nav label for the Reviews section. */
export const HOST_NAV_REVIEWS = "Reviews";

/** Sidebar nav label for the Notifications section. */
export const HOST_NAV_NOTIFICATIONS = "Notifications";

/** Section heading for the upcoming-bookings rail. */
export const UPCOMING_BOOKINGS_HEADING = "Upcoming Bookings";

/** "See all" / "View all" link label on the upcoming-bookings rail. */
export const VIEW_ALL = "View all";

/** CTA label for the empty-calendar "manage your availability" button. */
export const MANAGE_AVAILABILITY_CTA = "Manage Availability";

/** CTA label on the Stripe-connect attention callout / banner. */
export const CONNECT_STRIPE_CTA = "Connect Stripe";

/*
 * Attention-banner body descriptions shared between the web dashboard
 * banners (DashboardPage) and the mobile Today screen callouts (today.tsx).
 *
 * Web i18n key cross-references:
 *   payoutFailedDesc()            → dashboard.page.payout_failed_desc
 *   LISTINGS_ATTENTION_DESC       → dashboard.listings_attention_desc
 *   SUBSCRIPTION_PAST_DUE_DESC    → dashboard.subscription_past_due_desc
 */

/**
 * Body copy template for the payout-failed banner/callout, in i18next
 * interpolation form. The web passes this as the i18n default value for
 * dashboard.page.payout_failed_desc; mobile renders it via
 * `payoutFailedDesc()`.
 * Web en.json key: dashboard.page.payout_failed_desc
 */
export const PAYOUT_FAILED_DESC_TEMPLATE =
  "Stripe reported: {{reason}}. Please update your bank details so we can retry your payout.";

/**
 * English rendering of `PAYOUT_FAILED_DESC_TEMPLATE` for the (English-only)
 * mobile app.
 * Web en.json key: dashboard.page.payout_failed_desc
 */
export function payoutFailedDesc(reason: string): string {
  return PAYOUT_FAILED_DESC_TEMPLATE.replace("{{reason}}", reason);
}

/**
 * Body copy for the listings-need-attention banner/callout.
 * Web en.json key: dashboard.listings_attention_desc
 */
export const LISTINGS_ATTENTION_DESC =
  "Open your garage to finish setup, submit for review, or fix issues so guests can book.";

/**
 * Body copy for the subscription past-due banner/callout.
 * Web en.json key: dashboard.subscription_past_due_desc
 */
export const SUBSCRIPTION_PAST_DUE_DESC =
  "Update your payment method to keep your host benefits active.";
